import { useState } from 'react'

import { useSendOrder } from '@/hooks/useSendOrder'
import { formatHeld, type HeldSymbol } from '@/lib/positions'
import type { PlanRequest, PlanResponse, Side } from '@/lib/types'

/**
 * Bouton d'achat / de vente posé sous le graphique.
 *
 * **Un seul bouton est proposé à la fois, et c'est le graphique qui le
 * choisit.** Le stop placé au-dessus du prix d'entrée ne peut appartenir qu'à
 * une vente ; placé en dessous, qu'à un achat. Afficher les deux boutons
 * laisserait envoyer un ordre dont le stop est du mauvais côté — un ordre que
 * le courtier refuserait, ou pire, accepterait à l'envers.
 *
 * Le libellé porte toujours les deux lectures : ce que je décide (« ACHETER »)
 * et ce qui part réellement (« vend chez le courtier »). C'est l'application du
 * §2 du cahier des charges à l'endroit le plus dangereux de l'interface — celui
 * où l'on appuie.
 */
export function ChartOrderButton({
  side,
  request,
  plan,
  currency,
  brokerReady,
  held,
  refus,
}: {
  /** Sens de MA saisie, déduit de la position du stop sur le graphique. */
  side: Side
  request: PlanRequest | null
  plan: PlanResponse | null
  currency: string
  brokerReady: boolean
  /** Ce qui est déjà ouvert sur CETTE paire, rappelé avant d'en ajouter. */
  held?: HeldSymbol
  /*
   * Refus calculé à l'écran, avec ce qu'il sait et que le serveur ne regarde
   * qu'à l'envoi : le total déjà engagé sur les AUTRES paires. Le serveur
   * refuse de toute façon — c'est lui qui fait foi — mais l'attendre pour le
   * découvrir obligerait à appuyer pour apprendre qu'on ne peut pas.
   */
  refus?: string | null
}) {
  const [confirming, setConfirming] = useState(false)
  const order = useSendOrder()

  if (!brokerReady) return null

  const buy = side === 'BUY'
  const blocked = !plan?.is_sendable || !request || Boolean(refus)
  // Première erreur du plan : dire POURQUOI le bouton est inerte vaut mieux
  // qu'un bouton grisé sans explication.
  const reason = plan?.errors?.[0] ?? refus ?? null

  return (
    <>
      <button
        type="button"
        disabled={blocked}
        onClick={() => {
          order.reset()
          setConfirming(true)
        }}
        className={`flex min-h-touch flex-1 flex-col items-center justify-center rounded-md px-3 py-1 leading-tight disabled:opacity-40 ${
          buy ? 'bg-up/20 text-up active:bg-up/30' : 'bg-down/20 text-down active:bg-down/30'
        }`}
      >
        <span className="text-[14px] font-bold tracking-wide">
          {buy ? 'ACHETER' : 'VENDRE'}
          {plan && <span className="num ml-2 font-normal">{plan.volume.lots} lot</span>}
        </span>
        <span className="num text-[10px] font-normal text-ink-muted">
          {blocked
            ? (reason ?? 'ordre non envoyable')
            : `→ ${buy ? 'vend' : 'achète'} chez le courtier`}
        </span>
      </button>

      {confirming && (
        <ConfirmSheet
          held={held}
          side={side}
          plan={plan}
          currency={currency}
          order={order}
          onSend={() => request && order.send(request)}
          onClose={() => setConfirming(false)}
        />
      )}
    </>
  )
}

/**
 * Feuille de confirmation, en plein écran.
 *
 * Elle est modale et couvre la fenêtre parce que le bouton peut être appuyé
 * depuis un graphique lui-même en plein écran : un récapitulatif rendu plus bas
 * dans la page ne serait tout simplement pas vu.
 */
function ConfirmSheet({
  side,
  plan,
  currency,
  order,
  onSend,
  onClose,
  held,
}: {
  side: Side
  plan: PlanResponse | null
  currency: string
  order: ReturnType<typeof useSendOrder>
  onSend: () => void
  onClose: () => void
  held?: HeldSymbol
}) {
  const buy = side === 'BUY'

  return (
    /*
      Feuille CENTRÉE, et bornée à la hauteur réellement visible.
      ---------------------------------------------------------------------
      Elle était collée en bas sur téléphone. Dans Safari, la barre d'outils
      du navigateur recouvre le bas de la zone que `inset-0` désigne : les
      boutons « Envoyer » et « Annuler » se retrouvaient DERRIÈRE elle, et
      l'appui allait à la barre du navigateur. On voyait la feuille, on ne
      pouvait pas valider.

      `100dvh` mesure ce qui est visible à l'instant t, barres comprises ou
      non — contrairement à `inset-0`, qui décrit la zone de mise en page. Et
      centrer éloigne les boutons des deux bords à la fois : même là où la
      mesure serait imparfaite, ils restent au milieu de l'écran.

      Le panneau devient une colonne : en-tête et boutons fixes, contenu
      défilant entre les deux. Quelle que soit la longueur du récapitulatif —
      un message d'erreur s'ajoute, la note sur le spread s'allonge — le
      bouton d'envoi reste au même endroit et reste atteignable.
    */
    <div className="safe-top safe-bottom fixed inset-x-0 top-0 z-50 flex h-[100dvh] items-center justify-center bg-black/70 p-3">
      <div className="flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-xl border border-border bg-panel">
        <div className="shrink-0 border-b border-border px-4 py-3">
          <p className="text-[15px] font-semibold text-ink-strong">Confirmer l'envoi</p>
          <p className="mt-0.5 text-[11px] text-ink-muted">
            C'est le bloc de droite qui part chez le courtier.
          </p>
        </div>

        {order.result ? (
          <div className="min-h-0 overflow-y-auto p-4">
            <p className="rounded-md border border-up/40 bg-up/10 p-3 text-[13px] text-ink">
              {order.result}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 min-h-touch w-full rounded-md border border-border bg-elevated text-[14px] text-ink"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            {/* Tout ce qui se lit défile ; seuls les boutons restent fixes. */}
            <div className="min-h-0 flex-1 overflow-y-auto">
            {/* Les deux lectures, côte à côte — jamais l'une sans l'autre. */}
            <div className="grid grid-cols-2 gap-px bg-border">
              <Block
                title="Ma saisie"
                side={side}
                symbol={plan?.entry.symbol ?? '—'}
                lots={plan?.volume.lots ?? '—'}
                stop={plan?.entry.stop_loss_pips ?? null}
                target={plan?.entry.take_profit_pips ?? null}
                tone={buy ? 'up' : 'down'}
              />
              <Block
                title="Envoyé au courtier"
                side={buy ? 'SELL' : 'BUY'}
                symbol={plan?.sent.symbol ?? '—'}
                lots={plan?.volume.lots ?? '—'}
                stop={plan?.sent.stop_loss_pips ?? null}
                target={plan?.sent.take_profit_pips ?? null}
                tone={buy ? 'down' : 'up'}
                emphasise
              />
            </div>

            {/*
              Déjà engagé sur cette paire : le dire ICI, au dernier moment.
              C'est la seconde position sur une même paire qui double une
              exposition sans qu'on l'ait décidé — la liste des positions est
              dans un autre onglet, et on ne l'a pas sous les yeux en envoyant.
            */}
            {held && (
              <p className="mx-4 mt-3 rounded-md border border-warn/40 bg-warn/10 p-2 text-[12px] leading-relaxed text-ink">
                Vous avez déjà{' '}
                <strong className="text-ink-strong">
                  {held.count} position{held.count > 1 ? 's' : ''} ouverte
                  {held.count > 1 ? 's' : ''}
                </strong>{' '}
                sur cette paire ({formatHeld(held)}). Cet ordre s'y ajoute.
              </p>
            )}

            <dl className="space-y-1 px-4 py-3 text-[13px]">
              <Row
                label="Risque réel"
                value={plan?.risk_amount ? `−${plan.risk_amount} ${currency}` : '—'}
                tone="down"
              />
              <Row
                label="Gain visé"
                value={plan?.reward_amount ? `+${plan.reward_amount} ${currency}` : '—'}
                tone="up"
              />
              <Row
                label="Spread à l'ouverture"
                value={plan?.spread_cost ? `−${plan.spread_cost} ${currency}` : '—'}
                tone="down"
              />
              <Row label="Marge" value={`${plan?.margin_required ?? '—'} ${currency}`} />
            </dl>

            {/*
              Le rappel devient chiffré : le spread n'est plus « défavorable »
              en général, il vaut tant, et on le voit à côté du gain visé au
              moment précis où l'on décide d'envoyer.
            */}
            <p className="px-4 pb-3 text-[11px] leading-relaxed text-ink-muted">
              {plan?.spread_pips
                ? `Le spread (${plan.spread_pips} pips) se paie à l'ouverture, dans les deux sens : l'inversion ne le renverse pas. Commission et swap non plus.`
                : 'Spread, commission et swap restent défavorables dans les deux sens.'}
            </p>

            {order.error && (
              <p className="mx-4 mb-3 rounded-md border border-down/40 bg-down/10 p-2 text-[12px] text-down">
                {order.error}
              </p>
            )}
            </div>

            <div className="flex shrink-0 gap-2 border-t border-border p-3">
              <button
                type="button"
                onClick={onSend}
                disabled={order.busy}
                className="min-h-touch flex-1 rounded-md bg-accent text-[15px] font-bold text-white disabled:opacity-50"
              >
                {order.busy ? 'Envoi…' : 'Envoyer'}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={order.busy}
                className="min-h-touch flex-1 rounded-md border border-border bg-elevated text-[15px] text-ink-muted"
              >
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Block({
  title,
  side,
  symbol,
  lots,
  stop,
  target,
  tone,
  emphasise = false,
}: {
  title: string
  side: Side
  symbol: string
  lots: string
  stop: string | null
  target: string | null
  tone: 'up' | 'down'
  emphasise?: boolean
}) {
  return (
    <div className={`p-3 ${emphasise ? 'bg-elevated' : 'bg-panel'}`}>
      <p className="mb-1 text-[10px] uppercase tracking-wide text-ink-muted">{title}</p>
      <p className={`num text-[15px] font-bold ${tone === 'up' ? 'text-up' : 'text-down'}`}>
        {side === 'BUY' ? 'ACHAT' : 'VENTE'}
      </p>
      <p className="num text-[12px] text-ink">
        {symbol} · {lots} lot
      </p>
      <p className="num mt-1 text-[11px] text-ink-muted">SL {stop ?? '—'} p</p>
      <p className="num text-[11px] text-ink-muted">TP {target ?? '—'} p</p>
    </div>
  )
}

function Row({
  label,
  value,
  tone = 'normal',
}: {
  label: string
  value: string
  tone?: 'normal' | 'up' | 'down'
}) {
  const tones = { normal: 'text-ink', up: 'text-up', down: 'text-down' } as const
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={`num font-semibold ${tones[tone]}`}>{value}</dd>
    </div>
  )
}
